import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ActionsDropdownItem, ActionsMenu } from "@/js/components/ActionsMenu";
import { FeatureFlag } from "@/api/classroomFeatureFlags";
import { SendNewTabModal } from "@/js/components/SendNewTabModal";
import SendMessageModal from "@/js/components/SendMessageModal";
import { TabLimitModal } from "@/components/TabLimit/TabLimitModal";
import { getFeatures } from "stores/classwizeConfigStore";
import { useFeatureFlagsQuery } from "@/query/featureFlagsQuery";
import { useUserSessionStore } from "@/stores/v2/userSessionStore";
import { EditingFocus } from "@/components/FocusLibraryDrawer/FocusPage";

interface Props {
	openPauseModal: () => void;
	openFocusDrawer: () => void;
	openRewardModal: () => void;
	openBypassModal: () => void;
	openManageStudentsModal: () => void;
	openScreenshareModal: () => void;
	isClassActive: boolean;
	areAnyStudentsFocused: boolean;
	areAllStudentsFocused: boolean;
	studentsArePaused: boolean;
	classHasStudents: boolean;
	classSourceId: string;
	/** Display name for the classroom (e.g. from getClassroomDisplayName), used for analytics */
	classroomName: string;
	appliedFocuses?: EditingFocus[];
	tabLimitModalOpen?: boolean;
	/** (open, viaEdit?) - when true, viaEdit indicates modal was opened via the edit button (e.g. TabLimitPill) */
	setTabLimitModalOpen?: (open: boolean, viaEdit?: boolean) => void;
	/** True when modal was opened via edit button; used for tab_limit_adjusted analytics */
	tabLimitOpenedViaEdit?: boolean;
	onTabLimitApplied?: (limit: number) => void;
	/** Disables the Class tools menu trigger (e.g. while in seat plan selection) */
	disabled?: boolean;
	/** Tooltip shown when the menu trigger is disabled */
	disabledTooltip?: string;
}

export default function ClassToolsDropdown(props: Props): JSX.Element {
	const { t } = useTranslation();
	const [newTabModalOpen, setNewTabModalOpen] = useState(false);
	const [announcementModalOpen, setAnnouncementModalOpen] = useState(false);
	const [internalTabLimitModalOpen, setInternalTabLimitModalOpen] = useState(false);

	// Use external state if provided, otherwise use internal state
	const tabLimitModalOpen = props.tabLimitModalOpen !== undefined ? props.tabLimitModalOpen : internalTabLimitModalOpen;
	const setTabLimitModalOpen = props.setTabLimitModalOpen || setInternalTabLimitModalOpen;

	const userSessionStore = useUserSessionStore();
	const featureFlagsQuery = useFeatureFlagsQuery(userSessionStore.active_device as string);

	const classwizeFeatures = getFeatures();

	const isOpenTabDisabled = !props.isClassActive || !props.classHasStudents || props.areAnyStudentsFocused;
	const isAnnouncementDisabled = !props.isClassActive || !props.classHasStudents;
	const isTabLimitDisabled = !props.isClassActive || !props.classHasStudents;
	const isFocusDisabled = !props.classHasStudents || props.areAnyStudentsFocused;
	const isRewardDisabled = !props.isClassActive || !props.classHasStudents || props.areAnyStudentsFocused;
	const isPauseDisabled = !props.isClassActive || !props.classHasStudents;
	const isModifyStudentsDisabled = false;
	const isBypassCodeDisabled = !props.isClassActive || !props.classHasStudents || props.areAnyStudentsFocused;
	const isScreenShareDisabled = !props.isClassActive || !props.classHasStudents || props.areAllStudentsFocused;

	const focusDisabledTooltip = props.areAnyStudentsFocused ? t("classTools.focusTooltipOnlyOne") : undefined;
	const screenShareDisabledTooltip = props.areAllStudentsFocused ? t("classTools.screenShareTooltipFocused") : undefined;

	const menuItems: ActionsDropdownItem[] = useMemo(
		() => [
			{
				icon: "fa-browser",
				label: t("classTools.openTab"),
				dataTestId: "menu-button-open-site",
				show: classwizeFeatures.includes("open_tab"),
				action: () => setNewTabModalOpen(true),
				disabled: isOpenTabDisabled,
				tooltip: isOpenTabDisabled ? undefined : t("classTools.openTabTooltip"),
			},
			{
				icon: "fa-megaphone",
				label: t("classTools.announcement"),
				dataTestId: "menu-button-announcement",
				show: classwizeFeatures.includes("message_class"),
				action: () => setAnnouncementModalOpen(true),
				disabled: isAnnouncementDisabled,
				tooltip: isAnnouncementDisabled ? undefined : t("classTools.announcementTooltip"),
			},
			{
				icon: "fa-browsers",
				label: t("classTools.tabLimit"),
				dataTestId: "tab-limit-menu-button",
				show: classwizeFeatures.includes("tab_limit"),
				action: () => setTabLimitModalOpen(true, false),
				disabled: isTabLimitDisabled,
				tooltip: isTabLimitDisabled ? undefined : t("classTools.tabLimitTooltip"),
				iconVariant: "light",
			},
			{
				icon: "fa-arrows-to-eye",
				label: t("classTools.focus"),
				dataTestId: "menu-button-focus",
				show: classwizeFeatures.includes("focus"),
				action: props.openFocusDrawer,
				disabled: isFocusDisabled,
				tooltip: isFocusDisabled ? focusDisabledTooltip : t("classTools.focusTooltip"),
			},
			{
				icon: "fa-pause",
				label: t("classTools.pauseInternet"),
				dataTestId: "menu-button-pause",
				show: classwizeFeatures.includes("lock"),
				action: props.openPauseModal,
				disabled: isPauseDisabled,
				tooltip: isPauseDisabled ? undefined : t("classTools.pauseInternetTooltip"),
			},
			{
				icon: "fa-user",
				label: t("classTools.modifyStudents"),
				dataTestId: "menu-button-modify-students",
				show: classwizeFeatures.includes("add_user"),
				action: props.openManageStudentsModal,
				disabled: isModifyStudentsDisabled,
				tooltip: isModifyStudentsDisabled ? undefined : t("classTools.modifyStudentsTooltip"),
			},
			{
				icon: "fa-screencast",
				label: t("classTools.screenShare"),
				dataTestId: "menu-button-screen-share",
				show: !!featureFlagsQuery.data?.includes(FeatureFlag.SCREEN_SHARE),
				action: props.openScreenshareModal,
				disabled: isScreenShareDisabled,
				tooltip: isScreenShareDisabled ? screenShareDisabledTooltip : t("classTools.screenShareTooltip"),
			},
			{
				icon: "fa-key",
				label: t("classTools.bypassCode"),
				dataTestId: "menu-button-bypass-code",
				show: classwizeFeatures.includes("bypass_code"),
				action: props.openBypassModal,
				disabled: isBypassCodeDisabled,
				tooltip: isBypassCodeDisabled ? undefined : t("classTools.bypassCodeTooltip"),
			},
			{
				icon: "fa-gift",
				label: t("classTools.reward"),
				dataTestId: "menu-button-reward",
				show: classwizeFeatures.includes("reward"),
				action: props.openRewardModal,
				disabled: isRewardDisabled,
				tooltip: isRewardDisabled ? undefined : t("classTools.rewardTooltip"),
			},
		],
		[
			t,
			classwizeFeatures,
			isOpenTabDisabled,
			isAnnouncementDisabled,
			isTabLimitDisabled,
			isFocusDisabled,
			isPauseDisabled,
			isModifyStudentsDisabled,
			isScreenShareDisabled,
			isBypassCodeDisabled,
			isRewardDisabled,
			focusDisabledTooltip,
			screenShareDisabledTooltip,
			featureFlagsQuery.data,
			props,
			setTabLimitModalOpen,
		]
	);

	return (
		<>
			<ActionsMenu title={t("classTools.title")} items={menuItems} disabled={props.disabled} disabledTooltip={props.disabledTooltip} />
			<SendNewTabModal
				title={t("classTools.sendNewTabTitle")}
				classroomSourceId={props.classSourceId}
				open={newTabModalOpen}
				onClose={() => setNewTabModalOpen(false)}
			/>
			<SendMessageModal
				title={t("classTools.sendAnnouncementTitle")}
				allClassroomStudents={true}
				classroomSourceId={props.classSourceId}
				open={announcementModalOpen}
				onClose={() => setAnnouncementModalOpen(false)}
				maxMessageLength={500}
				subtitle={t("classTools.sendAnnouncementSubtitle")}
			/>
			<TabLimitModal
				open={tabLimitModalOpen}
				onClose={() => setTabLimitModalOpen(false)}
				appliedFocuses={props.appliedFocuses}
				onApply={props.onTabLimitApplied}
				classroomName={props.classroomName}
				openedViaEdit={props.tabLimitOpenedViaEdit}
			/>
		</>
	);
}
